import './dateTimeFormatPolyfill/polyfill.js';

import * as Sentry from '@sentry/react';
import { formatISO } from 'date-fns';
import compareAsc from 'date-fns/compareAsc';
import compareDesc from 'date-fns/compareDesc';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfDay from 'date-fns/startOfDay';

import { DEFAULT_DISCOVER_URL } from '../const';
import i18n from '../i18n';
import { HITS_TYPES_BY_URL } from '../pages/DiscoverPage/const';
import { USER_ROLES } from '../pages/const';
import {
  ContinueWatchingVideoLink,
  LearnerInfo,
  MySubscriptionB2BLearnerChargebeeInfo,
  MySubscriptionB2CLearnerChargebeeInfo,
  MySubscriptionCompanyOwnerChargebeeInfo,
  MySubscriptionTeamManagerChargebeeInfo,
  SubscriptionGroupType,
  Team,
  TeamRole,
  VideoCategory,
} from '../types';
import {
  CURRENCY_CODE_TO_SYMBOL,
  HEADER_IMAGES_URLS,
  VIDEO_MAX_PROGRESS,
  VIDEO_MIN_PROGRESS,
} from './const';
import GraphQlError from './errors/GraphQlError';

const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_MINUTE = 60;

const defaultDateTimeFormat = new Intl.DateTimeFormat('en', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

export const formatDateToNewsFormat = (dateString, dateTimeFormat = defaultDateTimeFormat) => {
  if (!dateString) return null;

  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th',
  };
  const date = new Date(dateString.replace(/-/g, '/'));

  if (!isValidDate(date)) {
    return null;
  }

  const [{ value: month }, , { value: day }, , { value: year }] =
    dateTimeFormat.formatToParts(date);

  let suffix = '';
  const dayNumber = Number(day);
  if (dayNumber) {
    const dozens = Math.floor(dayNumber / 10);
    const units = dayNumber % 10;
    if (dozens !== 1) {
      if (units === 1) {
        suffix = suffixes.one;
      } else if (units === 2) {
        suffix = suffixes.two;
      } else if (units === 3) {
        suffix = suffixes.few;
      } else {
        suffix = suffixes.other;
      }
    } else {
      suffix = suffixes.other;
    }
  }

  let formattedDay = `${day}${suffix}`;

  return `${formattedDay} ${month} ${year}`;
};

export const dateToReviewFormat = (date) => {
  const dateParsed = date ? parse(date, 'yyyy-MM-dd HH:mm:ss', new Date()) : null;
  return dateParsed ? format(dateParsed, 'dd MMM YYY') : '';
};

const isValidDate = (d) => {
  // @ts-ignore
  return d instanceof Date && !isNaN(d);
};

export const isUndefined = (variable) => {
  return typeof variable === 'undefined';
};

export const secondsToHms = (sec) => {
  const n = Number(sec);

  return [n / 3600, (n % 3600) / 60, (n % 3600) % 60]
    .map((v) => (v < 10 ? '0' + parseInt(String(v)) : parseInt(String(v))))
    .filter((i, j) => i !== '00' || j > 0)
    .join(':');
};

const sToHM = (n) => {
  const h = parseInt(String(n / SECONDS_IN_HOUR));
  const m = parseInt(String((n % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE));
  return [h, m];
};

const sToMS = (n) => {
  const s = n % SECONDS_IN_MINUTE;
  const m = parseInt(String(n / SECONDS_IN_MINUTE));
  return [m, s];
};

export const secondsToHHHmmm = (sec) => {
  const n = Number(sec);
  const [hours, minutes] = sToHM(n);

  let res = '';
  if (hours) {
    res += `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  if (minutes) {
    res += ` ${minutes} minute${minutes > 1 ? 's' : ''}`;
  }

  return res.trim();
};

const getUnits = (count, lessThanHour) => {
  const u1 = lessThanHour ? 'minuteShort' : 'hourShort';
  const u2 = lessThanHour ? 'secondShort' : 'minuteShort';

  return [translate('common.' + u1, { count }), translate('common.' + u2, { count })];
};

export const formatDuration = (seconds) => {
  const lessThanHour = seconds < SECONDS_IN_HOUR;
  const parseSeconds = lessThanHour ? sToMS : sToHM;

  const [val1, val2] = parseSeconds(seconds);

  let res = '';
  if (val1) {
    const [unit] = getUnits(val1, lessThanHour);
    res += `${val1} ${unit}`;
  }

  if (val2) {
    const [, unit] = getUnits(val2, lessThanHour);
    res += ` ${val2} ${unit}`;
  }

  return res.trim();
};

export const roundToDecimals = (value) => Math.ceil(value * 10) / 10;

export const TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

export const getTimeRange = (from = new Date(), to = new Date(), timeFormat = TIME_FORMAT) => ({
  from: format(startOfDay(from), timeFormat),
  to: format(endOfDay(to), timeFormat),
});

export const checkIsObject = (value) => typeof value === 'object' && value !== null;

type QueryStringParamsType = { [x: string]: string; [x: number]: string } | null;

export const getQueryStringParams = (value: string = ''): QueryStringParamsType => {
  let result: QueryStringParamsType = null;

  if (value) {
    const urlParams = new URLSearchParams(value);
    result = Object.fromEntries(urlParams);
  }

  return result;
};

export const convertToFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => formData.append(key, data[key]));
  return formData;
};

export const getLinksObj = (arr?: (ContinueWatchingVideoLink | null)[] | null) => {
  let res = {};
  if (arr && Array.isArray(arr)) {
    arr.forEach((item) => {
      if (item?.video?.id) {
        res[item?.video?.id] = {
          chapter: item?.chapter,
          time: item?.time,
        };
      }
    });
  }

  return res;
};

export const generateContinueWatchingLink = (
  url: string,
  chapter?: string | number | null,
  time?: string | number | null,
) => {
  if ((chapter || (chapter === 0 && time !== '0')) && time) {
    return `${url}?chapter=${chapter}&time=${time}`;
  } else {
    return url;
  }
};

/**
 *
 * @param lastPosition - last viewed position, in milliseconds
 * @param duration - video duration, in seconds
 * @param debug - display real position if lastPosition > duration
 * @return - object with video progress, and progressValue in percentages
 */
export const getVideoProgressData = (lastPosition, duration, debug: boolean = false) => {
  const _lastPosition = Math.ceil(lastPosition / 1000);
  const progress = _lastPosition > duration && !debug ? duration : _lastPosition;
  const progressValue = (progress / duration) * 100;
  return {
    progress,
    progressValue,
  };
};

export const getVideoProgressDataByCompleted = (
  lastPosition,
  duration,
  completed,
  debug: boolean = false,
) => {
  let progress;
  let progressValue;
  if (completed) {
    progress = duration;
    progressValue = 100;
  } else {
    const { progress: prg, progressValue: prgValue } = getVideoProgressData(
      lastPosition,
      duration,
      debug,
    );
    progress = prg;
    progressValue = prgValue;
  }

  return {
    progress,
    progressValue,
  };
};

export const calcVideoProgressAndLinkToShow = (
  completed,
  lastPosition,
  duration,
  videoUrl,
  chapter,
  time,
  debug = false,
) => {
  const { progressValue } = getVideoProgressData(lastPosition, duration, debug);

  let linkToShow = generateContinueWatchingLink(`/videos/${videoUrl}/`, chapter, time);
  let progressToShow = progressValue;

  if (!completed && progressValue > VIDEO_MAX_PROGRESS && progressValue < 100) {
    progressToShow = VIDEO_MAX_PROGRESS;
  }
  if (!completed && progressValue >= 100) {
    linkToShow = `/videos/${videoUrl}/`;
    progressToShow = VIDEO_MIN_PROGRESS;
  }
  return {
    linkToShow,
    progressToShow,
  };
};

//i18n helpers
export const translate = (key: string, vars?): string => i18n.t(key, { ...vars }) || key;
export const translateIfExists = (key: string, vars?): string | null => {
  if (i18n.exists(key)) return i18n.t(key, { ...vars });
  return null;
};

export const secondsToPathwaysFormat = (d) => {
  d = Number(d);
  let h = Math.floor(d / 3600);
  let m = Math.floor((d % 3600) / 60);

  let hDisplay = h > 0 ? `${h} h ` : '';
  let mDisplay = m > 0 ? `${m} m` : '';

  return hDisplay + mDisplay;
};

export const countResultStars = (resultPercent, stars: (string | null)[] | null = []) => {
  let starsCount = 0;
  const starsLoc = stars ? stars : [];
  const starsNumberArray = starsLoc.map((item) =>
    item ? item.split(' - ').map((item) => Number(item)) : [],
  );

  for (let i = 0; i < starsNumberArray.length; i++) {
    if (
      (starsNumberArray[i][0] <= resultPercent && starsNumberArray[i][1] > resultPercent) ||
      (i === starsNumberArray.length - 1 && starsNumberArray[i][0] <= resultPercent)
    ) {
      starsCount = i + 1;
      break;
    }
  }

  return starsCount;
};

export const replaceQuotes = (str: string) => str.replace(/"/g, '&#34;');

export const createCategoryLink = (category) => {
  if (!category || !category?.slug) return null;

  return category.parent
    ? `${DEFAULT_DISCOVER_URL}/${category?.parent.slug}/${category?.slug}`
    : `${DEFAULT_DISCOVER_URL}/${category?.slug}`;
};

export const generateSeoTitle = (title, tenantTitle) =>
  [title, tenantTitle].filter(Boolean).join(' | ');

export const generateRandomImageUrl = (tenantName?: string | null) => {
  if (process.env.REACT_APP_IS_E2E) {
    return HEADER_IMAGES_URLS.financeunlocked.at(1);
  }

  if (tenantName && HEADER_IMAGES_URLS?.[tenantName]) {
    const imageIndex = Math.floor(Math.random() * HEADER_IMAGES_URLS[tenantName].length);
    return HEADER_IMAGES_URLS[tenantName][imageIndex];
  } else {
    const imageIndex = Math.floor(Math.random() * HEADER_IMAGES_URLS['financeunlocked'].length);
    return HEADER_IMAGES_URLS['financeunlocked'][imageIndex];
  }
};

export const filterPathwaysArrByVideoId = (pathways, videoId: string) => {
  return pathways.find((pathway) => pathway.videos.find((video) => video?.id === videoId));
};

export const parseHighlightedText = (
  string,
  formatter = (match: string) => `<span>${match.slice(2, -2)}</span>`,
) => {
  if (string) {
    const pattern = new RegExp(/\*\*(?:(?!\*\*).)+\*\*/g);
    return string.replace(pattern, (match) => formatter(match));
  } else {
    return null;
  }
};

export const notEmpty = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

export function toBoolean<T>(item): item is T {
  return Boolean(item);
}

export const getTeamsWhereManager = (teams: (Team | null)[]): Team[] => {
  return teams
    .filter(notEmpty)
    .filter(
      (team) => team?.learner_role === TeamRole.Manager || team?.learner_role === TeamRole?.Owner,
    );
};

export const getTeamsWhereLearner = (teams: (Team | null)[]): Team[] => {
  return teams.filter(notEmpty).filter((team) => team?.learner_role === TeamRole.Member);
};

export const splitIntoChunks = (arr: any[] = [], chunkLength) => {
  const res: any[] = [];

  for (let i = 0; i < arr.length; i += chunkLength) {
    const tempArray = arr.slice(i, i + chunkLength);
    res.push(tempArray);
  }

  return res;
};

export const getSymbolFromCurrency = (code: String): string | undefined => {
  return CURRENCY_CODE_TO_SYMBOL[code.toLowerCase()];
};

const getCertificateBaseUrl = (tenantName) => `/certificates/${tenantName}/`;

export const getCertificatePDFUrl = (hash, tenantName) =>
  `${getCertificateBaseUrl(tenantName)}${hash}.pdf`;

export const getCertificatePNGUrl = (hash, tenantName) =>
  `${getCertificateBaseUrl(tenantName)}${hash}.png`;

export const getSocialShareImage = (hash, tenantName) =>
  `${getCertificateBaseUrl(tenantName)}social-img/${hash}.png`;

//TODO update when backend will be ready
const getCourseCertificateBaseUrl = (tenantName) => `/certificates/${tenantName}/courses/`;

export const getCourseCertificatePNGUrl = (hash, tenantName) =>
  `${getCourseCertificateBaseUrl(tenantName)}${hash}.png`;

export const getCourseSocialShareImage = (hash, tenantName) =>
  `${getCourseCertificateBaseUrl(tenantName)}social-img/${hash}.png`;

export const getCourseCertificatePDFUrl = (hash, tenantName) =>
  `${getCourseCertificateBaseUrl(tenantName)}${hash}.pdf`;

export const shuffleArray = (arr) => {
  const arrCopy = [...arr];

  return arrCopy.sort(() => Math.random() - 0.5);
};

export const checkIfInternalLink = (link: string): boolean => {
  return !!(link && link[0] === '/');
};

export const generateRandomString = (length: number): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;

  const result: string[] = [];

  for (let i = 0; i < length; i++) {
    result.push(characters.charAt(Math.floor(Math.random() * charactersLength)));
  }
  return result.join('');
};

export const sortByDate = (data, key, asc) => {
  return data.sort((a, b) => {
    if (asc) {
      return compareAsc(new Date(a[key] || 0), new Date(b[key] || 0));
    } else {
      return compareDesc(new Date(a[key] || 0), new Date(b[key] || 0));
    }
  });
};

type UpdateUserComPlanIntoType = {
  plan?: string | null;
  planType?: SubscriptionGroupType | null;
  renewDate?: string | null;
  trialLeft?: number | null;
  userInfo?: LearnerInfo | null;
};

export const updateUserComPlanInfo = ({
  plan,
  planType,
  renewDate,
  trialLeft = undefined,
  userInfo = undefined,
}: UpdateUserComPlanIntoType) => {
  if (window?.userengage) {
    let renewOn;
    if (renewDate) {
      try {
        renewOn = Date.parse(renewDate);
      } catch (e) {}
    }

    window.userengage('client.update', {
      ...(plan && { current_plan: plan }),
      ...(planType && { plan_type: planType }),
      has_subscription: !!userInfo?.has_subscription,
      had_subscription_bool: Boolean(userInfo?.had_subscription),
      ...(renewOn && {
        sub_end_date: formatISO(renewOn, { representation: 'date' }),
      }),
      ...(userInfo?.role && { role: getRoleForUserCom(userInfo) }),
      ...((userInfo?.role === USER_ROLES.CompanyOwner ||
        userInfo?.role === USER_ROLES.B2CLearner) && {
        is_on_trial: Boolean(trialLeft),
      }),
      ...(userInfo?.teams?.length && { team_id: userInfo?.teams?.[0]?.id }),
      ...(userInfo?.teams?.length && { team_name: userInfo?.teams?.[0]?.name }),
    });
  }
};

export const splitBySpace = (val) => (val ? val.split(' ') : []);

export const generateCertificateName = (tenant, pathwayName) => {
  return `${tenant}-${pathwayName}.pdf`;
};

export const onDownloadCertificate = async (certName, certUrl) => {
  const img = await fetch(certUrl);
  const imgBlob = await img?.blob();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(imgBlob);
  link.download = certName;
  link.click();
};

export const isAbsoluteUrl = (url = '') => {
  const reg = /^https?:\/\//i;
  return Boolean(reg.test(url));
};

export const pipe =
  (...fns) =>
  (data) =>
    [...fns].reduce((accData, fn) => fn(accData), data);
export const updateField = (key, updateFn) => (obj) => ({ ...obj, [key]: updateFn(obj) });

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const roundToFixed = (num: number, numsAfterDot: number) => {
  const t = Math.pow(10, numsAfterDot);

  return Math.round(num * t) / t;
};

export const getCourseUrl = (slug, isStarted = false) =>
  `/courses/${slug}${isStarted ? '/progress' : ''}`;

export const replaceByIndex = <T>(arr: T[], index: number, replacement: T): T[] => {
  const [before, after] = excludeByIndex(index, arr);

  return [...before, ...([replacement] as T[]), ...after];
};

const excludeByIndex = <T>(index: number, arr: T[]): T[][] => [
  [...arr.slice(0, index)],
  [...arr.slice(index + 1)],
];

export const removeNth = <T>(index: number, arr: T[]): T[] => {
  const [before, after] = excludeByIndex(index - 1, arr);

  return [...before, ...after];
};

export const removeLast = <T>(arr: T[]): T[] => {
  return removeNth(arr.length, arr);
};

export const toggleInArray = (item, array) => {
  const exist = array.includes(item);

  if (exist) {
    return array.filter((el) => el !== item);
  } else {
    return [...array, item];
  }
};

export const getUniqObjectsFromArray = <T>(arr: T[], getKey: (val: T) => any): T[] => {
  return Array.from(new Map(arr.map((item) => [getKey(item), item])).values());
};

export const downloadFile = async (fileName, fileUrl) => {
  const img = await fetch(fileUrl);
  const imgBlob = await img?.blob();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(imgBlob);
  link.download = fileName;
  link.click();
};

export const isSubscriptionOwnerInfo = (
  info:
    | MySubscriptionCompanyOwnerChargebeeInfo
    | MySubscriptionB2BLearnerChargebeeInfo
    | MySubscriptionTeamManagerChargebeeInfo
    | MySubscriptionB2CLearnerChargebeeInfo,
): MySubscriptionB2CLearnerChargebeeInfo | MySubscriptionCompanyOwnerChargebeeInfo | null => {
  if (
    info?.__typename === 'MySubscriptionCompanyOwnerChargebeeInfo' ||
    info?.__typename === 'MySubscriptionB2CLearnerChargebeeInfo'
  ) {
    return info;
  }

  return null;
};

export const sendEventToGTM = (event, variables = {}) => {
  // @ts-ignore
  if (window?.dataLayer) {
    // @ts-ignore
    window.dataLayer.push({
      event,
      ...variables,
    });
  }
};

export const sendDataToGTM = (variables = {}) => {
  // @ts-ignore
  if (window?.dataLayer) {
    // @ts-ignore
    window.dataLayer.push({
      ...variables,
    });
  }
};

//used to send custom GTM event on user.com form submission
export const handleUserComPayload = (userInfo) => (message) => {
  try {
    const data = JSON.parse(message);
    if (data?.type === 'gtm-event' && data?.value === 'subscription-purchased') {
      if (
        (userInfo?.role === USER_ROLES.CompanyOwner || userInfo?.role === USER_ROLES.B2CLearner) &&
        userInfo?.has_subscription
      ) {
        sendEventToGTM(
          data?.value,
          typeof data?.dataLayerVariables === 'object' ? data?.dataLayerVariables : undefined,
        );
      }
    } else if (data?.type === 'gtm-event' && data?.value) {
      sendEventToGTM(
        data?.value,
        typeof data?.dataLayerVariables === 'object' ? data?.dataLayerVariables : undefined,
      );
    }
  } catch (e) {
    Sentry.captureException(e);
  }
};

export const isIOSDevice = () => {
  //@ts-ignore
  return /iPad|iPhone|iPod/.test(window?.navigator?.userAgent) && !window?.MSStream;
};

export const isScreenRotated = () => {
  const orientation =
    //@ts-ignore
    (window?.screen?.orientation || {})?.type ||
    //@ts-ignore
    window?.screen?.mozOrientation ||
    //@ts-ignore
    window?.screen?.msOrientation;
  if (orientation) {
    return orientation === 'landscape-primary' || orientation === 'landscape-secondary';
  } else if (window?.orientation || window?.orientation === 0) {
    return window.orientation === 90 || window.orientation === -90;
  } else {
    return window?.innerWidth > window?.innerHeight;
  }
};

export const getRoleForUserCom = (userInfo: LearnerInfo | null | undefined) => {
  switch (userInfo?.role) {
    case USER_ROLES.CompanyOwner:
      return 'Company Owner';
    case USER_ROLES.TeamManager:
      return 'Manager';
    case USER_ROLES.B2BLearner:
      return 'B2B learner';
    case USER_ROLES.B2CLearner:
      return 'B2C learner';
    default:
      return null;
  }
};

export const handleGraphQlError = (error, operation) => {
  if (process.env.NODE_ENV === 'production') {
    if (operation?.operationName) {
      Sentry.setTag('operation_name', operation?.operationName);
    }
    if (operation?.variables) {
      Sentry.addBreadcrumb({
        category: 'log',
        message: `Request - "${operation?.operationName}". Variables: `,
        data: operation.variables,
      });
    }
    Sentry.captureException(new GraphQlError(JSON.stringify(error)));
  }
};

export const getHasAuthCookie = (cookies) => {
  if (cookies) {
    const keys = Object.keys(cookies);
    const authCookieRegExp = new RegExp(/^auth0\..+\.is\.authenticated$/);

    let res: string | null = null;
    keys.forEach((item) => {
      const match = item.match(authCookieRegExp);
      if (match && match[0]) {
        res = match[0];
      }
    });

    return res && cookies[res] === 'true';
  }

  return false;
};

export const collectValueByKey = (key, valueKey) => (acc, item) => {
  const targetKey = item[key];
  const targetValue = item[valueKey];
  return {
    ...acc,
    [targetKey]: targetValue,
  };
};

export const checkIsLocalStorageBlocked = () => {
  try {
    localStorage.setItem('testKey', 'test');
    localStorage.removeItem('testKey');
    return false;
  } catch (e) {
    return true;
  }
};

export const formatProgressData = (progress) => {
  if (!progress || !progress.length) return {};

  return progress?.reduce((all, elem) => {
    const id = elem?.video?.id;
    const videoUrl = elem?.video?.video_url || id;
    const { linkToShow, progressToShow } = calcVideoProgressAndLinkToShow(
      elem?.completed,
      elem?.last_position,
      elem?.video?.duration,
      videoUrl,
      elem?.chapter,
      elem?.time,
    );
    return {
      ...all,
      [id as any]: {
        link: linkToShow,
        progress: progressToShow,
        completed: elem?.completed,
        new: Boolean(elem?.video?.new),
      },
    };
  }, {});
};
export const sleep = (timeout) => new Promise((resolve) => setTimeout(resolve, timeout));

export const checkIsIframed = () => window?.location !== window?.parent?.location;

export function isElementAboveViewport(element, offset = 0) {
  if (!element) {
    return false;
  }

  const { bottom } = element.getBoundingClientRect();
  return bottom + offset <= 0;
}

export const getCrypto = () => {
  //ie 11.x uses msCrypto
  return (window.crypto || (window as any).msCrypto) as Crypto;
};

export const getCryptoSubtle = () => {
  const crypto = getCrypto();
  //safari 10.x uses webkitSubtle
  return crypto.subtle || (crypto as any).webkitSubtle;
};

export const checkIsSSR = () => !!window?.isSSR;

export const calcSerpType = (pathname = '') => {
  let type;
  const typeRegExp = new RegExp('[^/]+/?$', 'g');
  const match = pathname.match(typeRegExp);
  if (match && match[0]) {
    type = match[0].replace(/\/$/, '');
  }

  return HITS_TYPES_BY_URL[type];
};

export const getCategoryUrl = (category: VideoCategory | null) =>
  `${DEFAULT_DISCOVER_URL}/${category?.parent?.slug || ''}${category?.slug}`;
